import React from 'react';
import {
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  Chip,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { Service, TimeSlot, TeamMember } from '../../types';
import { CustomerFormData } from './CustomerInfoForm';
import { formatDisplayTime } from '../../utils/dateUtils';
import { useTranslation } from 'react-i18next';

interface BookingSummaryProps {
  service: Service;
  date: Date;
  timeSlot: TimeSlot;
  customerInfo: CustomerFormData;
  teamMember?: TeamMember | null;
}

const BookingSummary: React.FC<BookingSummaryProps> = ({
  service,
  date,
  timeSlot,
  customerInfo,
  teamMember,
}) => {
  const { t } = useTranslation();
  const startTime = timeSlot.startTime;
  const endTime = timeSlot.endTime;

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h5" component="h2" gutterBottom>
        {t('bookingSummary.title')}
      </Typography>
      <Divider sx={{ mb: 2 }} />

      <List disablePadding>
        <ListItem sx={{ py: 1, px: 0 }}>
          <ListItemText
            primary={t('bookingSummary.service')}
            secondary={service.name}
          />
        </ListItem>

        <ListItem sx={{ py: 1, px: 0 }}>
          <ListItemText
            primary={t('bookingSummary.date')}
            secondary={format(date, 'EEEE, MMMM d, yyyy')}
          />
        </ListItem>

        {teamMember && (
          <ListItem sx={{ py: 1, px: 0 }}>
            <ListItemText
              primary={t('bookingSummary.stylist')}
              secondary={teamMember.name}
            />
          </ListItem>
        )}

        <ListItem sx={{ py: 1, px: 0 }}>
          <ListItemText
            primary={t('bookingSummary.time')}
            secondary={`${formatDisplayTime(startTime)} - ${formatDisplayTime(endTime)}`}
          />
          <Chip
            label={`${service.duration} ${t('common.min')}`}
            size="small"
            color="primary"
            variant="outlined"
          />
        </ListItem>

        <Divider sx={{ my: 1 }} />

        <ListItem sx={{ py: 1, px: 0 }}>
          <ListItemText
            primary={t('bookingSummary.client')}
            secondary={`${customerInfo.firstName} ${customerInfo.lastName}`}
          />
        </ListItem>

        <ListItem sx={{ py: 1, px: 0 }}>
          <ListItemText
            primary={t('bookingSummary.contact')}
            secondary={
              <>
                <Typography variant="body2" component="span">{customerInfo.email}</Typography>
                <br />
                <Typography variant="body2" component="span">{customerInfo.phone}</Typography>
              </>
            }
          />
        </ListItem>

        {customerInfo.notes && (
          <ListItem sx={{ py: 1, px: 0 }}>
            <ListItemText
              primary={t('bookingSummary.notes')}
              secondary={customerInfo.notes}
            />
          </ListItem>
        )}

      </List>
    </Paper>
  );
};

export default BookingSummary;
